import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ArrowBack from "@mui/icons-material/ArrowBack";
import MenuIcon from "@mui/icons-material/Menu";
import SearchIcon from "@mui/icons-material/Search";
import "../../styles/common/header.scss";
import Breadcrumbs from "./Breadcrumbs";
import Cookies from "js-cookie";
import { HomeOutlinedIcon } from "../../icons";

const Header = ({
  headerName,
  onToggleMobileMenu,
  mobileMenuOpen,
  searchQuery,
  setSearchQuery,
  isShowSearch,
  breadcrumbData,
  customBreadcrumbLinks,
  setHeaderName,
  setIsShowSearch,
  headerExtra,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const paths = location.pathname.split("/").filter((val) => val !== "");
  const role = Cookies.get("role");
  const [materialsQuestionBankData, setMaterialsQuestionBankData] =
    useState(breadcrumbData);

  const shouldShowBreadcrumbs =
    paths.length > 1 &&
    (paths.includes("course") ||
      paths.includes("courses") ||
      paths.includes("materials") ||
      paths.includes("question-bank") ||
      paths.includes("eval") ||
      paths.includes("reports") ||
      paths.includes("videos") ||
      (paths[0] === "teacher" && paths[1] === "homework"));

  const goUpPath = role === "admin" ? "/admin/courses" : "/institution/courses";

  const courseKey =
    paths[1] === "course" || paths[1] === "eval" ? paths[2] : paths[3];
  const subjectKey = paths[3] === "subject" ? paths[4] : paths[5];
  const topicKey = paths[5] === "topic" ? paths[6] : paths[7];
  const subTopicKey =
    paths[7] === "subtopic" || paths[7] === "subTopic" ? paths[8] : paths[9];

  const handleBack = () => {
    navigate(-1);
  };

  const isInstitutionReportsRoute =
    paths?.[0] === "institution" && paths?.[1] === "reports";

  const shouldShowBackButton = (() => {
    if (paths?.[1] === "videos") {
      return (
        Array.isArray(customBreadcrumbLinks) && customBreadcrumbLinks.length > 0
      );
    }
    if (!isInstitutionReportsRoute) {
      return paths?.length > 2;
    }

    const p = location.pathname;

    // Only show back icon on institution "View Reports" pages, not on main drill-down list pages.
    // Examples to show:
    // - /institution/reports/course/:courseKey
    // - /institution/reports/course/:courseKey/batch/:batchId/reports
    // - /institution/reports/course/:courseKey/batch/:batchId/teacher/:teacherId
    // - /institution/reports/course/:courseKey/batch/:batchId/student/:studentId
    // Examples to hide:
    // - /institution/reports
    // - /institution/reports/course/:courseKey/batches
    // - /institution/reports/course/:courseKey/batch/:batchId

    if (/^\/institution\/reports\/course\/[^/]+$/.test(p)) return true;
    if (
      /^\/institution\/reports\/course\/[^/]+\/batch\/[^/]+\/reports$/.test(p)
    )
      return true;
    if (
      /^\/institution\/reports\/course\/[^/]+\/batch\/[^/]+\/teacher\/[^/]+$/.test(
        p,
      )
    )
      return true;
    if (
      /^\/institution\/reports\/course\/[^/]+\/batch\/[^/]+\/student\/[^/]+$/.test(
        p,
      )
    )
      return true;

    return false;
  })();

  const findNameInBreadcrumbData = (data, key) => {
    if (!data || !key) return null;
    if (data.subject && Array.isArray(data.subject)) {
      for (const subjectObj of data.subject) {
        if (subjectObj[key]) {
          return subjectObj[key];
        }
      }
    }

    if (data?.topic && Array.isArray(data.topic)) {
      for (const topicObj of data.topic) {
        for (const subjectKey in topicObj) {
          if (Array.isArray(topicObj[subjectKey])) {
            for (const topicItem of topicObj[subjectKey]) {
              for (const topicKey in topicItem) {
                if (
                  topicKey === key &&
                  topicKey !== "next_count" &&
                  topicKey !== "page_count"
                ) {
                  return topicItem[topicKey];
                }
              }
            }
          }
        }
      }
    }

    if (data.subtopic && Array.isArray(data.subtopic)) {
      for (const subtopicObj of data.subtopic) {
        for (const topicKey in subtopicObj) {
          if (Array.isArray(subtopicObj[topicKey])) {
            for (const subtopicItem of subtopicObj[topicKey]) {
              for (const subtopicKey in subtopicItem) {
                if (subtopicKey === key && subtopicKey !== "page_count") {
                  return subtopicItem[subtopicKey];
                }
              }
            }
          }
        }
      }
    }

    return null;
  };

  const generateBreadcrumbLinks = () => {
    const baseLinks =
      role === "admin"
        ? [
            {
              icon: <HomeOutlinedIcon />,
              label: "Home",
              path: "/admin/courses",
            },
          ]
        : [
            {
              icon: <HomeOutlinedIcon />,
              label: "Home",
              path: "/institution/courses",
            },
          ];

    const courseBaseSegment = paths[1] === "eval" ? "eval" : "course";

    if (
      paths.length >= 2 &&
      paths[0] === "teacher" &&
      paths[1] === "homework"
    ) {
      const params = new URLSearchParams(location.search);
      let courseQP =
        params.get("course") ||
        params.get("courseid") ||
        params.get("course_id") ||
        params.get("courseKey") ||
        params.get("course_key");
      let subjectQP =
        params.get("subject") ||
        params.get("subjectid") ||
        params.get("subject_id") ||
        params.get("subjectKey") ||
        params.get("subject_key");
      let topicQP =
        params.get("topic") ||
        params.get("topicid") ||
        params.get("topic_id") ||
        params.get("topicKey") ||
        params.get("topic_key");
      let subtopicQP =
        params.get("subtopic") ||
        params.get("subtopicid") ||
        params.get("subtopic_id") ||
        params.get("subtopicKey") ||
        params.get("subtopic_key");

      if (!courseQP && paths.length >= 4 && paths[2] === "course" && paths[3]) {
        courseQP = paths[3];
      }
      if (
        !subjectQP &&
        paths.length >= 6 &&
        paths[4] === "subject" &&
        paths[5]
      ) {
        subjectQP = paths[5];
      }
      if (!topicQP && paths.length >= 8 && paths[6] === "topic" && paths[7]) {
        topicQP = paths[7];
      }
      if (
        !subtopicQP &&
        paths.length >= 10 &&
        paths[8] === "subtopic" &&
        paths[9]
      ) {
        subtopicQP = paths[9];
      }

      const baseForTeacher = [
        {
          icon: <HomeOutlinedIcon />,
          label: "Home",
          path: "/teacher/homework",
        },
      ];

      if (!courseQP) {
        setIsShowSearch(false);
        return baseForTeacher;
      }

      const data =
        (breadcrumbData && Array.isArray(breadcrumbData)
          ? breadcrumbData[0]
          : null) ||
        (materialsQuestionBankData && Array.isArray(materialsQuestionBankData)
          ? materialsQuestionBankData[0]
          : null);
      const courseName = data?.course_name || `Course ${courseQP}`;
      const subjectName = subjectQP
        ? findNameInBreadcrumbData(data, subjectQP) || `Subject ${subjectQP}`
        : null;
      const topicName = topicQP
        ? findNameInBreadcrumbData(data, topicQP) || `Topic ${topicQP}`
        : null;
      const subtopicName = subtopicQP
        ? findNameInBreadcrumbData(data, subtopicQP) || `Subtopic ${subtopicQP}`
        : null;

      const linkFor = (q) => {
        const preserved = new URLSearchParams();
        const originals = new URLSearchParams(location.search);
        originals.forEach((v, k) => {
          if (
            ![
              "course",
              "courseid",
              "course_id",
              "courseKey",
              "course_key",
              "subject",
              "subjectid",
              "subject_id",
              "subjectKey",
              "subject_key",
              "topic",
              "topicid",
              "topic_id",
              "topicKey",
              "topic_key",
              "subtopic",
              "subtopicid",
              "subtopic_id",
              "subtopicKey",
              "subtopic_key",
            ].includes(k)
          ) {
            preserved.set(k, v);
          }
        });
        Object.entries(q).forEach(([k, v]) => {
          if (v !== null && v !== undefined && v !== "") preserved.set(k, v);
        });
        const qs = preserved.toString();
        return `/teacher/homework${qs ? `?${qs}` : ""}`;
      };

      setIsShowSearch(false);
      if (!subjectQP) {
        return [
          ...baseForTeacher,
          { label: courseName, path: linkFor({ course: courseQP }) },
          { label: "Batches", active: true },
        ];
      }

      if (!topicQP) {
        return [
          ...baseForTeacher,
          { label: courseName, path: linkFor({ course: courseQP }) },
          { label: "Batches", path: linkFor({ course: courseQP }) },
          { label: subjectName, active: true },
        ];
      }

      if (!subtopicQP) {
        return [
          ...baseForTeacher,
          { label: courseName, path: linkFor({ course: courseQP }) },
          { label: "Batches", path: linkFor({ course: courseQP }) },
          {
            label: subjectName,
            path: linkFor({ course: courseQP, subject: subjectQP }),
          },
          { label: topicName, active: true },
        ];
      }

      return [
        ...baseForTeacher,
        { label: courseName, path: linkFor({ course: courseQP }) },
        { label: "Batches", path: linkFor({ course: courseQP }) },
        {
          label: subjectName,
          path: linkFor({ course: courseQP, subject: subjectQP }),
        },
        {
          label: topicName,
          path: linkFor({
            course: courseQP,
            subject: subjectQP,
            topic: topicQP,
          }),
        },
        { label: subtopicName, active: true },
      ];
    }

    if (paths.length >= 2 && paths[1] === "videos") {
      const base = [
        {
          icon: <HomeOutlinedIcon />,
          label: "Home",
          path: "/teacher/dashboard",
        },
      ];
      setHeaderName("Videos");
      setIsShowSearch(false);

      if (
        Array.isArray(customBreadcrumbLinks) &&
        customBreadcrumbLinks.length > 0
      ) {
        return base.concat(customBreadcrumbLinks);
      }

      return base;
    }

    if (paths.length >= 2 && paths[1] === "reports") {
      const base = [...baseLinks];
      setHeaderName("Reports");
      setIsShowSearch(false);

      if (
        Array.isArray(customBreadcrumbLinks) &&
        customBreadcrumbLinks.length > 0
      ) {
        return base.concat(customBreadcrumbLinks);
      }

      return base;
    }

    if (paths.length >= 2 && paths[1] === "materials") {
      const baselink = baseLinks;
      baselink[0].path = "/admin/materials";
      const materialsBaseLinks = [baselink[0]];
      if (paths.length >= 4 && paths[2] === "course" && paths[3]) {
        const courseKey = paths[3];
        const courseData = materialsQuestionBankData
          ? materialsQuestionBankData[0]
          : null;
        const courseName = courseData
          ? courseData.course_name
          : `Course ${courseKey}`;

        if (paths.length === 4) {
          setIsShowSearch(true);
          return [
            ...materialsBaseLinks,
            {
              label: courseName,
              active: true,
            },
          ];
        } else if (paths.length >= 6 && paths[4] === "subject" && paths[5]) {
          const subjectKey = paths[5];
          const subjectName = courseData
            ? findNameInBreadcrumbData(courseData, subjectKey) ||
              `Subject ${subjectKey}`
            : `Subject ${subjectKey}`;

          if (paths.length === 6) {
            setIsShowSearch(true);
            return [
              ...materialsBaseLinks,
              {
                label: courseName,
                path: `/admin/materials/course/${courseKey}`,
              },
              {
                label: subjectName,
                active: true,
              },
            ];
          } else if (paths.length >= 8 && paths[6] === "topic" && paths[7]) {
            const topicKey = paths[7];
            const topicName = courseData
              ? findNameInBreadcrumbData(courseData, topicKey) ||
                `Topic ${topicKey}`
              : `Topic ${topicKey}`;

            if (paths.length === 8) {
              setIsShowSearch(true);
              return [
                ...materialsBaseLinks,
                {
                  label: courseName,
                  path: `/admin/materials/course/${courseKey}`,
                },
                {
                  label: subjectName,
                  path: `/admin/materials/course/${courseKey}/subject/${subjectKey}`,
                },
                {
                  label: topicName,
                  active: true,
                },
              ];
            } else if (
              paths.length >= 10 &&
              paths[8] === "subtopic" &&
              paths[9]
            ) {
              const subtopicKey = paths[9];
              const subtopicName = courseData
                ? findNameInBreadcrumbData(courseData, subtopicKey) ||
                  `Subtopic ${subtopicKey}`
                : `Subtopic ${subtopicKey}`;

              setIsShowSearch(true);
              return [
                ...materialsBaseLinks,
                {
                  label: courseName,
                  path: `/admin/materials/course/${courseKey}`,
                },
                {
                  label: subjectName,
                  path: `/admin/materials/course/${courseKey}/subject/${subjectKey}`,
                },
                {
                  label: topicName,
                  path: `/admin/materials/course/${courseKey}/subject/${subjectKey}/topic/${topicKey}`,
                },
                {
                  label: subtopicName,
                  active: true,
                },
              ];
            }
          }
        }
      }
      return materialsBaseLinks;
    }

    if (paths.length >= 2 && paths[1] === "question-bank") {
      const baselink = baseLinks;
      baselink[0].path = "/admin/question-bank";
      const questionBankBaseLinks = [baselink[0]];
      if (paths.length >= 4 && paths[2] === "course" && paths[3]) {
        const courseKey = paths[3];
        const courseData = materialsQuestionBankData
          ? materialsQuestionBankData[0]
          : null;
        const courseName = courseData
          ? courseData.course_name
          : `Course ${courseKey}`;

        if (paths.length === 4) {
          setIsShowSearch(true);
          return [
            ...questionBankBaseLinks,
            {
              label: courseName,
              active: true,
            },
          ];
        } else if (paths.length >= 6 && paths[4] === "subject" && paths[5]) {
          const subjectKey = paths[5];
          const subjectName = courseData
            ? findNameInBreadcrumbData(courseData, subjectKey) ||
              `Subject ${subjectKey}`
            : `Subject ${subjectKey}`;

          if (paths.length === 6) {
            setIsShowSearch(true);
            return [
              ...questionBankBaseLinks,
              {
                label: courseName,
                path: `/admin/question-bank/course/${courseKey}`,
              },
              {
                label: subjectName,
                active: true,
              },
            ];
          } else if (paths.length >= 8 && paths[6] === "topic" && paths[7]) {
            const topicKey = paths[7];
            const topicName = courseData
              ? findNameInBreadcrumbData(courseData, topicKey) ||
                `Topic ${topicKey}`
              : `Topic ${topicKey}`;

            if (paths.length === 8) {
              return [
                ...questionBankBaseLinks,
                {
                  label: courseName,
                  path: `/admin/question-bank/course/${courseKey}`,
                },
                {
                  label: subjectName,
                  path: `/admin/question-bank/course/${courseKey}/subject/${subjectKey}`,
                },
                {
                  label: topicName,
                  active: true,
                },
              ];
            } else if (
              paths.length >= 10 &&
              paths[8] === "subtopic" &&
              paths[9]
            ) {
              const subtopicKey = paths[9];
              const subtopicName = courseData
                ? findNameInBreadcrumbData(courseData, subtopicKey) ||
                  `Subtopic ${subtopicKey}`
                : `Subtopic ${subtopicKey}`;

              setIsShowSearch(true);
              return [
                ...questionBankBaseLinks,
                {
                  label: courseName,
                  path: `/admin/question-bank/course/${courseKey}`,
                },
                {
                  label: subjectName,
                  path: `/admin/question-bank/course/${courseKey}/subject/${subjectKey}`,
                },
                {
                  label: topicName,
                  path: `/admin/question-bank/course/${courseKey}/subject/${subjectKey}/topic/${topicKey}`,
                },
                {
                  label: subtopicName,
                  active: true,
                },
              ];
            }
          }
        }
      }

      return questionBankBaseLinks;
    }

    if (
      paths.length >= 3 &&
      (paths[1] === "course" || paths[1] === "eval") &&
      breadcrumbData
    ) {
      try {
        const courseData = breadcrumbData[0];
        if (courseData && courseData.course_name) {
          setHeaderName("Courses");
          setIsShowSearch(true);
          if (paths.length === 3) {
            return [
              ...baseLinks,
              {
                label: courseData.course_name,
                active: true,
              },
            ];
          } else if (paths.length >= 5 && paths[3] === "subject") {
            const subjectKey = paths[4];
            let subjectName = `Subject ${subjectKey}`;

            if (courseData.subject && Array.isArray(courseData.subject)) {
              for (const subjectObj of courseData.subject) {
                if (subjectObj[subjectKey]) {
                  subjectName = subjectObj[subjectKey];
                  break;
                }
              }
            }

            if (paths.length === 5) {
              setHeaderName(`Subject Details - ${subjectName}`);
              setIsShowSearch(true);
              return [
                ...baseLinks,
                {
                  label: courseData.course_name,
                  path: `/${role}/${courseBaseSegment}/${paths[2]}`,
                },
                {
                  label: subjectName,
                  active: true,
                },
              ];
            } else if (paths.length >= 7 && paths[5] === "topic") {
              const subjectKey = paths[4];
              const topicKey = paths[6];
              let subjectName = `Subject ${subjectKey}`;
              let topicName = `Topic ${topicKey}`;

              if (courseData.subject && Array.isArray(courseData.subject)) {
                for (const subjectObj of courseData.subject) {
                  if (subjectObj[subjectKey]) {
                    subjectName = subjectObj[subjectKey];
                    break;
                  }
                }
              }

              if (courseData.topic && Array.isArray(courseData.topic)) {
                for (const topicObj of courseData.topic) {
                  if (
                    topicObj[subjectKey] &&
                    Array.isArray(topicObj[subjectKey])
                  ) {
                    for (const topicItem of topicObj[subjectKey]) {
                      for (const [key, name] of Object.entries(topicItem)) {
                        if (
                          key === topicKey &&
                          key !== "next_count" &&
                          key !== "page_count"
                        ) {
                          topicName = name;
                          break;
                        }
                      }
                    }
                  }
                }
              }

              if (paths.length === 7) {
                setHeaderName(`Topic Details - ${topicName}`);
                setIsShowSearch(true);
                return [
                  ...baseLinks,
                  {
                    label: courseData.course_name,
                    path: `/${role}/${courseBaseSegment}/${paths[2]}`,
                  },
                  {
                    label: subjectName,
                    path: `/${role}/${courseBaseSegment}/${paths[2]}/subject/${subjectKey}`,
                  },
                  {
                    label: topicName,
                    active: true,
                  },
                ];
              } else if (
                paths.length >= 9 &&
                (paths[7] === "subtopic" || paths[7] === "subTopic")
              ) {
                const subjectKey = paths[4];
                const topicKey = paths[6];
                const subtopicKey = paths[8];
                let subjectName = `Subject ${subjectKey}`;
                let topicName = `Topic ${topicKey}`;
                let subtopicName = `Subtopic ${subtopicKey}`;

                if (courseData.subject && Array.isArray(courseData.subject)) {
                  for (const subjectObj of courseData.subject) {
                    if (subjectObj[subjectKey]) {
                      subjectName = subjectObj[subjectKey];
                      break;
                    }
                  }
                }

                if (courseData.topic && Array.isArray(courseData.topic)) {
                  for (const topicObj of courseData.topic) {
                    if (
                      topicObj[subjectKey] &&
                      Array.isArray(topicObj[subjectKey])
                    ) {
                      for (const topicItem of topicObj[subjectKey]) {
                        for (const [key, name] of Object.entries(topicItem)) {
                          if (
                            key === topicKey &&
                            key !== "next_count" &&
                            key !== "page_count"
                          ) {
                            topicName = name;
                            break;
                          }
                        }
                      }
                    }
                  }
                }

                if (courseData.subtopic && Array.isArray(courseData.subtopic)) {
                  for (const subtopicObj of courseData.subtopic) {
                    if (
                      subtopicObj[topicKey] &&
                      Array.isArray(subtopicObj[topicKey])
                    ) {
                      for (const subtopicItem of subtopicObj[topicKey]) {
                        for (const [key, name] of Object.entries(
                          subtopicItem,
                        )) {
                          if (key === subtopicKey && key !== "page_count") {
                            subtopicName = name;
                            break;
                          }
                        }
                      }
                    }
                  }
                }
                setHeaderName(`Subtopic Details - ${subtopicName}`);
                setIsShowSearch(true);

                return [
                  ...baseLinks,
                  {
                    label: courseData.course_name,
                    path: `/${role}/${courseBaseSegment}/${paths[2]}`,
                  },
                  {
                    label: subjectName,
                    path: `/${role}/${courseBaseSegment}/${paths[2]}/subject/${subjectKey}`,
                  },
                  {
                    label: topicName,
                    path: `/${role}/${courseBaseSegment}/${paths[2]}/subject/${subjectKey}/topic/${topicKey}`,
                  },
                  {
                    label: subtopicName,
                    active: true,
                  },
                ];
              }
            }
          }
        }
      } catch (error) {
        console.error("Error processing breadcrumb data:", error);
      }
    }

    const dynamicLinks = [];

    return [...baseLinks, ...dynamicLinks];
  };

  const breadcrumbLinks = generateBreadcrumbLinks();

  useEffect(() => {
    if (breadcrumbData) {
      setMaterialsQuestionBankData(breadcrumbData);
    }
  }, [breadcrumbData]);

  return (
    <header className={`header ${mobileMenuOpen ? "mobile-menu-open" : ""}`}>
      <div className="header-container">
        <div className="header-content">
          <div className="header-title-container">
            {shouldShowBackButton && (
              <button
                className="back-button"
                onClick={handleBack}
                aria-label="Go Back"
              >
                <ArrowBack sx={{ fontSize: 20 }} />
              </button>
            )}
            <div className="header-logo">
              <h3 style={{ margin: "5px 0", marginBottom: "5px" }}>
                {headerName}
              </h3>
            </div>
          </div>
          <div className="header-actions">
            <button
              className="mobile-menu-toggle"
              onClick={() => onToggleMobileMenu && onToggleMobileMenu()}
              aria-label="Toggle menu"
            >
              <MenuIcon fontSize="small" />
            </button>
          </div>
          {isShowSearch && (
            <div className="header-search-with-extra">
              {headerExtra}
              <label className="toolbar-search">
                <SearchIcon fontSize="small" className="search-icon" />
                <input
                  type="text"
                  placeholder={"Search"}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </label>
            </div>
          )}
        </div>
        {shouldShowBreadcrumbs && (
          <Breadcrumbs
            links={breadcrumbLinks}
            showActions={false}
            goUpPath={paths?.length === 2 ? goUpPath : null}
            showGoUpButton={paths.length > 2}
            showDropdowns={
              !(
                paths.includes("videos") ||
                (paths[0] === "teacher" && paths[1] === "homework")
              )
            }
            breadcrumbData={
              breadcrumbData &&
              Array.isArray(breadcrumbData) &&
              breadcrumbData.length > 0
                ? breadcrumbData[0]
                : materialsQuestionBankData &&
                    Array.isArray(materialsQuestionBankData) &&
                    materialsQuestionBankData.length > 0
                  ? materialsQuestionBankData[0]
                  : null
            }
            courseKey={courseKey}
            subjectKey={subjectKey}
            topicKey={topicKey}
            subTopicKey={subTopicKey}
          />
        )}
      </div>
    </header>
  );
};

export default Header;
